/**
 * Publishes a summary of the current seats.io session.
 * @namespace ReservedSeating
 * @augments Publisher
 */

/**
 * @typedef {object} ReservedSeating
 * @property {string[]]} allListingSeatLabels - all seatLabels that are part of any known listing
 * @property {string[]]} allListingSeatPrices - all prices that are part of any known listing
 * @property {string[]]} listingSeatLabels - any seats currently reserved for the active resale listing
 * @property {string} seatingChartHoldId - the unique identifier for the session
 * @property {string} seatingChartHoldToken - the unique identifier for the session
 */
inherit("ReservedSeating", {
	parent: `Publisher`,
	singleton: true,
	requires: [`App`, `Countdown`, `DOM`, `Events`, `Time`, `XHR`],
	prepare(setup) {
		const { data, instances, promises } = this;

		// used to keep track of instances that require an active seats.io session
		instances.subscribers = [];

		// used to deal with multiple requests before the session is established
		promises.session = null;

		// set initial values for internal reference
		this.assign(data, {
			allListingSeatLabels: [],
			eventId: null,
			expirationOverride: null,
			gaReservedSaleIds: [],
			listingSeatLabels: [],
			seatingChartHold: null,
			selectedObjects: [],
			timeoutId: null, // reference to the expiration handler timeout id
		});

		// set the initial values for the published digest
		this.update({
			allListingSeatLabels: [],
			listingSeatLabels: [],
			seatingChartHoldId: null,
			seatingChartHoldToken: null,
		});
	},
	constructor(setup) {
		const { DOM } = this;

		DOM.on({
			event: `beforeunload`,
			target: window,
			matchContainer: false,
			handler: (evt) => {
				// window is closing, release any reserved tickets, etc.
				this.endSession();
			},
		});
	},
	api: {
		/**
		 * Ensures that a seats.io session is initialized before continuing.
		 * If an active session is detected for the given event, it is reused.
		 * Also registers the requesting entity when keeping track of potential subscribers.
		 *
		 * @async
		 * @method requireSession
		 * @param {object} context - The calling context that needs a session
		 * @param {object} options - Options for controlling session creation
		 * @param {boolean} [options.canReserveSeats=false] - Indicates if the session can reserve seats
		 * @param {number} [options.eventId] - Identifies which event to attach the session to
		 * @returns {Promise<void>} Resolves when the session is established or reused
		 */
		requireSession: {
			async: true,
			public: true,
			defaults: {
				canReserveSeats: false,
			},
			value(context, options) {
				const { Events, data, instances, promises } = this;
				const { session: sessionPromise } = promises;
				const { canReserveSeats, eventId } = options;
				const { gaReservedSaleIds } = data;
				const { subscribers } = instances;

				subscribers.push({
					canReserveSeats,
					instance: context,
				});

				if (eventId === data.eventId && this.isPromise(sessionPromise)) {
					// a session is in the process of being created
					return sessionPromise;
				}

				if (data.seatingChartHold !== null) {
					if (data.eventId !== null && data.eventId !== eventId) {
						// existing session, wrong event -- reset the session
						this.endSession({ deleteHold: true });
					}
				}

				// NOTE: use explicit data.seatingChartHold, in case endSession() changed its value
				if (data.seatingChartHold === null) {
					data.eventId = eventId; // immediately update for future comparisons

					const eventPromise = Events.get({ id: eventId });

					const sessionPromise = this.when({
						promise: eventPromise,
						then: (event) => {
							const { sales } = event;

							gaReservedSaleIds.length = 0; // clear out the reserved sale IDs

							this.each(sales, (sale) => {
								const { id: saleId, seatingType, zone } = sale;

								if (this.isObject(zone) && seatingType === `OPEN`) {
									gaReservedSaleIds.push(saleId);
								}
							});

							return this.startSession({ eventId });
						},
						fin: () => (promises.session = null), // clear the cache
					});

					promises.session = sessionPromise;
					return sessionPromise;
				} // otherwise resolve immediately
			},
		},

		/**
		 * Initializes a new seats.io session for the given event.
		 * @async
		 * @method startSession
		 * @param {object} options - Options for controlling session creation
		 * @param {number} options.eventId - Identifies which event to attach the session to
		 * @returns {Promise<void>} Resolves when the session is established
		 */
		startSession: {
			async: true,
			async value(options) {
				await this.require({ deps: [`Listings`] });

				const { Listings, data } = this;
				const { eventId } = options;
				const compactEventListingsPromise = Listings.getCompactEventListings({ eventId });
				const seatingChartHoldPromise = this.createSeatingChartHold({ eventId });

				const [compactEventListings, seatingChartHold] = await Promise.all([
					compactEventListingsPromise,
					seatingChartHoldPromise,
				]);

				this.assign(data, { compactEventListings, eventId, seatingChartHold });
				this.reconcile();
			},
		},

		/**
		 * Obtains a new seats.io session from the BE server, wrapping in our waitroom UI.
		 * @async
		 * @method createSeatingChartHold
		 * @param {object} options - Options for controlling session creation
		 * @param {number} options.eventId - Identifies which event to attach the session to
		 * @returns {Promise<object>} Resolves with the new session details
		 */
		createSeatingChartHold: {
			async: true,
			value(options) {
				const { App, XHR } = this;
				const { eventId } = options;
				const url = `/api/events/${eventId}/seating-chart-holds`;
				const type = `POST`;
				const REST = true;

				let canceled = false;

				const waitingRoom = App.useWaitingRoom({
					onCancel: () => (canceled = true),
					promise: new Promise((resolve, reject) => {
						const placeRequest = async (requestId) => {
							try {
								const body = requestId === undefined ? {} : { requestId };
								const response = await XHR.ajax({ body, REST, type, url });
								const delay = response?.delay;

								if (!this.isObject(response.delay)) {
									resolve(response);
									return;
								}

								// delay object indicates that this request must be retried
								const { cb, requestId: delayRequestId, warning } = delay;
								const seconds = this.isInt(cb, 0) ? cb : 1; // default to 1s
								const duration = seconds * 1000; // convert to milliseconds

								// update the waiting list message
								waitingRoom.update({ message: warning });

								// wait for the specified amount of time
								await this.defer({ duration });

								if (!canceled) {
									// avoid placing future calls if the request was canceled
									return placeRequest(delayRequestId);
								}
							} catch (reason) {
								reject(reason);
							}
						};

						return placeRequest();
					}),
				});

				return waitingRoom.promise;
			},
		},

		/**
		 * Updates the list of selected objects from the seating chart immediately on change.
		 * @method replaceSelectedObjects
		 * @param {object} context - The calling context that needs a session
		 * @param {string[]} selectedObjectsUpdate - The updated list of selected objects
		 */
		replaceSelectedObjects: {
			public: true,
			value(context, selectedObjectsUpdate) {
				const { data } = this;
				const { selectedObjects } = data;

				// reset the array
				selectedObjects.length = 0;

				if (this.isArray(selectedObjectsUpdate)) {
					this.each(selectedObjectsUpdate, (object) => selectedObjects.push(object));
				}

				this.reconcile();
			},
		},

		/**
		 * Requests the best available seats from the BE server.
		 * @async
		 * @method getBestAvailable
		 * @param {object} context - The instance that called this method
		 * @param {object} options - Options for controlling the request
		 * @param {string[]} options.accessCodes - The list of access codes to use
		 * @param {string} options.groupSaleToken - The token for a group sale
		 * @param {object[]} options.items - The list of items to reserve
		 * @param {string} options.mode - The mode for the request (PURCHASE | UPGRADE)
		 * @param {string} options.privateLinkToken - The token for a private link
		 * @returns {Promise<object>} Resolves with the best available seats
		 */
		getBestAvailable: {
			async: true,
			public: true,
			defaults: {
				mode: `PURCHASE`, // PURCHASE | UPGRADE
			},
			async value(context, options) {
				const { XHR, data } = this;
				const { accessCodes, groupSaleToken, items, mode, privateLinkToken, priceListId } = options;
				const { eventId, seatingChartHold } = data;
				const { id: seatingChartHoldId } = seatingChartHold;
				const urlQueryParams = [];
				const body = { accessCodes, eventId, items, seatingChartHoldId };

				switch (mode) {
					case `FAN_PREVIEW`:
					case `UPGRADE`:
						urlQueryParams.push(`mode=${mode}`);
						break;
				}

				if (this.isString(privateLinkToken, 1)) {
					body.privateLinkToken = privateLinkToken;
				}

				if (this.isString(groupSaleToken, 1)) {
					body.groupSaleToken = groupSaleToken;
				}

				// this lets the BE filter out DO_NOT_SELL tickets
				if (this.isInt(priceListId)) {
					body.priceListId = priceListId;
				}

				const urlBase = `/api/events/${eventId}/seating-chart-holds/${seatingChartHoldId}/best-available`;
				const joinedQueryParams = urlQueryParams.join(`&`);
				const urlQueryString = joinedQueryParams.length > 0 ? `?${joinedQueryParams}` : ``;
				const url = `${urlBase}${urlQueryString}`;
				const type = `PUT`;
				const REST = true;

				const response = await XHR.ajax({ body, REST, type, url });
				return response;
			},
		},

		/**
		 * Reserves the the provided list of seatLabels.
		 * @async
		 * @method reserveSeats
		 * @param {object} context - The instance that called this method
		 * @param {object} options - Options for controlling the request
		 * @param {boolean} options.disableMaxQuantities - Indicates if max quantities should be disabled
		 * @param {string[]} options.seatLabels - The list of seat labels to reserve
		 * @returns {Promise<void>} Resolves when the seats are reserved
		 */
		reserveSeats: {
			async: true,
			public: true,
			async value(context, options) {
				const { data, instances } = this;
				const { selectedObjects } = data;
				const { disableMaxQuantities = false, seatLabels } = options;
				const labelsToSelect = this.clone(seatLabels);

				// extract any seatLabels that have already been selected
				this.extract(labelsToSelect, (seatLabel) => selectedObjects.includes(seatLabel));

				if (labelsToSelect.length > 0) {
					// there are still seatLabels to reserve, try using the seating chart instance
					const { subscribers } = instances;
					const seatingChartSubscriber = this.find(subscribers, { canReserveSeats: true });

					if (seatingChartSubscriber === undefined) {
						throw new Error(`No active seating chart found.`);
					}

					const { instance: seatingChart } = seatingChartSubscriber;

					// manually insert the seatLabels immediately, instead of waiting for seating chart to report the change
					this.each(labelsToSelect, (seatLabel) => selectedObjects.push(seatLabel));

					if (disableMaxQuantities) {
						await seatingChart.disableMaxQuantities();
					}

					try {
						await seatingChart.selectObjects({ seatLabels: labelsToSelect });
					} catch (error) {
						// manually remove the number of seatLabels that were pushed above
						this.each(labelsToSelect, () => selectedObjects.pop());
						throw error;
					}
				}
			},
		},

		/**
		 * Releases the provided list of seatLabels.
		 * @async
		 * @method releaseSeats
		 * @param {object} context - The instance that called this method
		 * @param {object} options - Options for controlling the request
		 * @param {string[]} options.seatLabels - The list of seat labels to release
		 * @returns {Promise<void>} Resolves when the seats are released
		 */
		releaseSeats: {
			async: true,
			public: true,
			async value(context, options) {
				const { XHR, data } = this;
				const { seatLabels } = options;
				const { eventId, seatingChartHold } = data;

				if (!this.isObject(seatingChartHold)) {
					throw new Error(`Seating chart session not found.`);
				}

				const { holdToken } = seatingChartHold;
				const seatLabelsString = seatLabels.join(`,`);
				const holdTokenParam = encodeURIComponent(holdToken); // uri encode any special characters
				const seatLabelsParam = encodeURIComponent(seatLabelsString); // uri encode any special characters
				const queryString = `holdToken=${holdTokenParam}&seatLabels=${seatLabelsParam}`;

				await XHR.ajax({
					block: false, // don't block the UI
					type: `DELETE`,
					url: `/api/events/${eventId}/seats?${queryString}`,
				});
			},
		},

		/**
		 * Reserves the provided list of seatLabels for a listing.
		 * @async
		 * @method reserveListing
		 * @param {object} context - The instance that called this method
		 * @param {object} options - Options for controlling the request
		 * @param {string[]} options.seatLabels - The list of seat labels to reserve
		 * @param {string[]} options.selectedSeatLabels - The list of seat labels that are already selected
		 * @returns {Promise<void>} Resolves when the seats are reserved
		 */
		reserveListing: {
			async: true,
			public: true,
			async value(context, options) {
				const { data } = this;
				const { listingSeatLabels } = data;
				const { seatLabels = [], selectedSeatLabels = [] } = options;

				try {
					await this.clearShoppingCart();

					// seats that are already selected
					selectedSeatLabels.forEach((seatLabel) => {
						if (!listingSeatLabels.includes(seatLabel)) {
							listingSeatLabels.push(seatLabel);
						}
					});

					if (seatLabels.length > 0) {
						const seatsToReserve = seatLabels.filter((seatLabel) => {
							return !listingSeatLabels.includes(seatLabel);
						});

						// NOTE: update the digest value first, so the seating chart can get the update before selecting seats
						this.update({ listingSeatLabels });

						if (seatsToReserve.length > 0) {
							await this.reserveSeats({ disableMaxQuantities: true, seatLabels: seatsToReserve });
							this.each(seatsToReserve, (seatLabel) => listingSeatLabels.push(seatLabel));
						}
					}
				} catch (error) {
					const errorMessage = error?.message;
					const defaultMessage = `Sorry, this listing is no longer available.`;
					const message = this.isString(errorMessage, 1) ? errorMessage : defaultMessage;

					await this.releaseActiveListing();
					throw new Error(message);
				}
			},
		},

		/**
		 * Reserves the listing based on a seatLabel (handler for clicking a seat on the chart).
		 * @async
		 * @method reserveListingBySeatLabel
		 * @param {object} context - The instance that called this method
		 * @param {object} options - Options for controlling the request
		 * @param {string} options.saleId - The unique identifier for the sale
		 * @param {string} options.seatLabel - The seat label to reserve
		 * @param {boolean} [options.alreadySelected=true] - Whether to assume the seat is already selected
		 * @returns {Promise<void>} Resolves when the seats are reserved
		 */
		reserveListingBySeatLabel: {
			async: true,
			public: true,
			defaults: {
				alreadySelected: true,
			},
			async value(context, options) {
				const { App, Listings, data } = this;
				const { alreadySelected, saleId, seatLabel } = options;
				const { compactEventListings, eventId, listingSeatLabels, seatingChartHold } = data;
				const { id: seatingChartHoldId } = seatingChartHold;
				const additionalSeatLabels = [];
				let isCompactListing;
				let listingId;
				let listing;

				try {
					// reset any existing seat labels
					listingSeatLabels.length = 0;

					if (alreadySelected) {
						listingSeatLabels.push(seatLabel);
					}

					await this.clearShoppingCart();

					this.find(compactEventListings, (compactEventListing, compactEventListingId) => {
						const { items: compactListingItems } = compactEventListing;
						const matchingItem = this.find(compactListingItems, { seatLabel });

						if (matchingItem !== undefined) {
							listingId = compactEventListingId;
							listing = compactEventListing;
							isCompactListing = true;
							return true; // break loop
						}
					});

					if (listing === undefined) {
						// immediately publish the update, since there will be an async gap
						this.reconcile();

						// listing not found in the compact list, try to look it up individually
						listing = await Listings.getBySeatLabel({ eventId, saleId, seatLabel });
						listingId = listing.id;
						isCompactListing = false;
					}

					const { items } = listing;
					let clickedSeatLabelFound = false; // used to skip the initially clicked object

					this.each(items, (item) => {
						const itemSeatLabel = isCompactListing ? item.seatLabel : item.seat.label;

						if (this.isString(itemSeatLabel, 1)) {
							// item with a seat selection
							if (itemSeatLabel === seatLabel && !clickedSeatLabelFound) {
								// the clicked object should already be selected, ignore its first occurrance
								clickedSeatLabelFound = true;

								if (!alreadySelected) {
									// add to the complete list of listing seat labels
									listingSeatLabels.push(itemSeatLabel);
								}
							} else {
								// add to the complete listing seatLabel list, and the list of additional ones to secure
								listingSeatLabels.push(itemSeatLabel);
								additionalSeatLabels.push(itemSeatLabel);
							}
						}
					});

					if (listingSeatLabels.length === 0) {
						// listing doesn't contain any items to reserve
						throw new Error(`Sorry, this listing is no longer available.`);
					}

					// publish the latest listingSeatLabels changes
					this.reconcile();

					if (additionalSeatLabels.length > 0) {
						await this.reserveSeats({ disableMaxQuantities: true, seatLabels: additionalSeatLabels });
					}

					// open the listing overlay
					await App.loadOverlay({
						setup: {
							eventId,
							seatingChartHoldId,
							listingId,
							saleId,
							overlayMode: `modal`,
							style: `ux4`,
							size: `small`,
						},
						type: `EventListing`,
					});
				} catch (error) {
					// release any seats that were reserved in the process
					await this.releaseActiveListing();

					// re-throw the error
					throw error;
				}
			},
		},

		preloadListingBySeatLabel: {
			public: true,
			value(context, options) {
				const { Listings, data, promises } = this;
				const { saleId, seatLabel } = options;
				const { compactEventListings, eventId } = data;
				const { preloadListing: preloadListingPromise } = promises;

				// NOTE: run in sequence, to avoid making multiple requests that end up being fo the same listing
				const promise = this.when({
					promise: preloadListingPromise,
					then: async () => {
						const matchingListing = this.find(compactEventListings, ({ items }) => {
							const matchingItem = this.find(items, { seatLabel });

							return this.isObject(matchingItem);
						});

						if (!this.isObject(matchingListing)) {
							const listing = await Listings.getBySeatLabel({ eventId, saleId, seatLabel });
							const { id: listingId, items: listingItems } = listing;
							const items = this.map(listingItems, ({ price, seat }) => {
								const { label: seatLabel } = seat;

								return { price, seatLabel };
							});

							compactEventListings[listingId] = { items, saleId };
							this.reconcile();
						}
					},
				});

				promises.preloadListing = promise;
			},
		},

		/**
		 * Releases the currently selected listing, unless a resale checkout is active.
		 * @async
		 * @method releaseActiveListing
		 * @param {object} context - The instance that called this method
		 * @returns {Promise<void>} Resolves when the seats are released
		 */
		releaseActiveListing: {
			public: true,
			async value(context) {
				const { data, instances } = this;
				const { subscribers } = instances;
				const { expirationOverride, listingSeatLabels } = data;

				if (expirationOverride !== null) {
					// an active checkout has taken over, don't release the seats
					return;
				}

				if (listingSeatLabels.length > 0) {
					await this.releaseSeats({ seatLabels: listingSeatLabels });

					const seatingChartSubscriber = this.find(subscribers, { canReserveSeats: true });
					if (seatingChartSubscriber !== undefined) {
						const { instance: seatingChart } = seatingChartSubscriber;

						// re-enable normal max quantity restrictions
						await seatingChart.enableMaxQuantities();
					}

					// reset the array
					listingSeatLabels.length = 0;

					// publish any potential changes
					this.reconcile();
				}
			},
		},

		/**
		 * Updates the seating chart hold expiration time.
		 * @method
		 * @param {object} context - The instance that called this method
		 * @param {number} expiration - The new expiration time
		 */
		setExpiration: {
			public: true,
			value(context, expiration) {
				const { data } = this;
				const { timeoutId } = data;

				window.clearTimeout(timeoutId);
				data.expirationOverride = expiration;
				this.reconcile();
			},
		},

		/**
		 * Checks for any existing items in the standard cart, and clear them upon user confirmation.
		 * @async
		 * @method clearShoppingCart
		 */
		clearShoppingCart: {
			async value() {
				await this.require({ deps: [`ShoppingCart`] });
				const { ShoppingCart } = this;

				const cleared = await ShoppingCart.clearContents({
					confirm: true,
					message: `Resale tickets can't be purchased with your current cart. Would you like to remove your carted tickets and continue?`,
				});

				if (!cleared) {
					// user declined to clear their cart
					throw new Error(`Resale tickets cannot be purchased with items in cart.`);
				}
			},
		},

		/**
		 * Checks if the current session is still in use, and ended if there are no subscribers or reserved objects.
		 * @method checkSessionUsage
		 */
		checkSessionUsage: {
			value() {
				const { data, instances } = this;
				const { expirationOverride, selectedObjects } = data;
				const { subscribers } = instances;
				const ignoreExpiration = this.isInt(expirationOverride);

				if (
					!ignoreExpiration && // another entity has taken over (e.g. ShoppingCart)
					subscribers.length === 0 && // no charts or other entities are paying attention
					selectedObjects.length === 0 // no objects have been selected
				) {
					this.endSession({ deleteHold: true });
				}
			},
		},

		/**
		 * Ends the current session, releasing any reserved seats and clearing the session data.
		 * @method endSession
		 * @param {object} context - The instance that called this method
		 * @param {object} options - Options for controlling the session end
		 * @param {boolean} options.deleteHold - Indicates if the session hold should be deleted
		 */
		endSession: {
			public: true,
			value(context, options = {}) {
				const { Countdown, XHR, data, instances } = this;
				const { eventId, expirationOverride, listingSeatLabels, seatingChartHold, timeoutId } = data;
				const { subscribers } = instances;
				let { deleteHold } = options;

				if (deleteHold === undefined) {
					// default to true unless a checkout process has already taken over
					deleteHold = expirationOverride === null;
				}

				subscribers.length = 0; // reset the array of subscribers to this session

				if (deleteHold && eventId !== null && this.isObject(seatingChartHold)) {
					const { id: holdId } = seatingChartHold;

					// avoid the possibility of deleting any session once checkout has begun
					XHR.ajax({
						type: `DELETE`,
						url: `/api/events/${eventId}/seating-chart-holds/${holdId}`,
					});
				}

				Countdown.clearExpiration();
				data.selectedObjects.length = 0; // reset the array of selected objects

				// instantly clear the session
				window.clearTimeout(timeoutId);

				this.assign(data, {
					eventId: null,
					expirationOverride: null,
					seatingChartHold: null,
					timeoutId: null, // reference to the expiration handler timeout id
				});

				// clear the array of seats reserved for a listing
				listingSeatLabels.length = 0;

				this.reconcile();
			},
		},

		/**
		 * Calculates the latest digest based on the internal publisher state.
		 * @method reconcile
		 */
		reconcile: {
			value() {
				const { Countdown, Time, data } = this;
				const {
					compactEventListings,
					expirationOverride,
					gaReservedSaleIds,
					listingSeatLabels,
					seatingChartHold,
					timeoutId,
				} = data;
				const currentTime = Time.getTime();
				const allListingSeatLabels = [];
				const allListingSeatPrices = [];
				const value = this.get();

				if (this.isObject(seatingChartHold)) {
					const {
						expiration: holdExpiration,
						holdToken: seatingChartHoldToken,
						id: seatingChartHoldId,
					} = seatingChartHold;
					const expiration = this.isInt(expirationOverride) ? expirationOverride : holdExpiration;

					if (expiration === null || expiration < currentTime) {
						// NOTE: no need to delete if expired
						this.endSession({ deleteHold: false });
					} else if (expiration !== value.expiration) {
						const duration = expiration - currentTime;

						window.clearTimeout(timeoutId);
						data.timeoutId = window.setTimeout(this.endSession, duration);
						Countdown.setExpiration(expiration);
					}

					this.each(compactEventListings, ({ items, saleId }) => {
						if (gaReservedSaleIds.includes(saleId)) {
							// don't provide details about GA reserved sales,
							// it confuses the seats.io chart to have multiple prices, etc.
							return;
						}

						this.each(items, ({ seatLabel, price }) => {
							if (this.isString(seatLabel, 1)) {
								const matchingSummary = this.find(allListingSeatPrices, { price });

								if (matchingSummary === undefined) {
									allListingSeatPrices.push({ price, seatLabels: [seatLabel] });
								} else {
									const { seatLabels } = matchingSummary;

									seatLabels.push(seatLabel);
								}
							}

							allListingSeatLabels.push(seatLabel);
						});
					});

					this.assign(value, {
						expiration,
						seatingChartHoldId,
						seatingChartHoldToken,
					});
				} else {
					// clear the arrays of associated seatLabels
					allListingSeatLabels.length = 0;
					listingSeatLabels.length = 0;

					this.assign(value, {
						expiration: null,
						seatingChartHoldId: null,
						seatingChartHoldToken: null,
					});
				}

				this.assign(value, { allListingSeatLabels, allListingSeatPrices, listingSeatLabels });

				this.update(value);
			},
		},

		/**
		 * Generates a summary of a single seat object.
		 * @method makeSeatSummary
		 * @param {object} context - The calling context
		 * @param {object} options - Options for controlling the summary
		 * @param {string} options.category - The category of the seat
		 * @param {boolean} [options.isMultiple=false] - Indicates if the seat is part of a group
		 * @param {string} [options.row=null] - The row of the seat
		 * @param {string} options.seat - The seat number
		 * @param {string} [options.section=null] - The section of the seat
		 * @param {boolean} [options.includeHTML=true] - Indicates if the summary should include HTML
		 * @returns {string} The summary of the single seat object
		 */
		makeSeatSummary: {
			public: true,
			defaults: {
				category: null,
				isMultiple: false,
				row: null,
				seat: null,
				section: null,
			},
			value(context, options) {
				const { category, isMultiple, row, seat, section, includeHTML = true } = options;
				let result = ``;

				if (!this.isString(seat)) {
					return result;
				}

				// section
				if (this.isString(section, 1)) {
					if (includeHTML) {
						result += `Section <strong class="section-value"><var section_number>${section}</var></strong>, `;
					} else {
						result += `Section <var section_number>${section}</var>, `;
					}
				}

				// row
				if (this.isString(row, 1)) {
					result += category === `TABLE` ? `Table ` : `Row `;
					if (includeHTML) {
						result += `<strong class="row-value"><var row_number>${row}</var></strong>, `;
					} else {
						result += `<var row_number>${row}</var>, `;
					}
				}

				// seat
				if (category === `BOOTH`) {
					result += `Booth `;
				} else if (category === `TABLE`) {
					result += this.isString(row, 1) ? `Seat ` : `Table `;
				} else {
					result += `Seat `;
				}

				if (includeHTML) {
					result += `<strong class="seat-value"><var seat_number>${seat}</var></strong>`;
				} else {
					result += `<var seat_number>${seat}${isMultiple ? `` : `</var>`}`;
				}

				return result;
			},
		},

		/**
		 * Generates a summary of multiple seat objects.
		 * @method makeSeatSummaryMultiple
		 * @param {object} context - The calling context
		 * @param {object} options - Options for controlling the summary
		 * @param {string} options.category - The category of the seats
		 * @param {object[]} options.seatObjects - The list of seat objects
		 * @returns {string} The summary of the multiple seat objects
		 */
		makeSeatSummaryMultiple: {
			public: true,
			defaults: {
				category: null,
				seatObjects: [],
			},
			value(context, options) {
				const { category, seatObjects } = options;

				if (this.isArray(seatObjects, 1)) {
					if (seatObjects.length === 1) {
						return this.makeSeatSummary({ ...seatObjects[0], category, includeHTML: false });
					} else {
						// multiple seats
						const { row, section } = seatObjects[0];
						const matching = this.filter(seatObjects, { row, section });
						let result = ``;

						result = this.makeSeatSummary({ ...seatObjects[0], category, includeHTML: false, isMultiple: true });

						for (let i = 1; i < matching.length; i++) {
							if (i === matching.length - 1) {
								result = `${result} & ${matching[i].seat}`;
							} else {
								result = `${result}, ${matching[i].seat}`;
							}
						}

						if (matching.length < seatObjects.length) {
							result = `${result}...`;
						}

						result = `${result}</var>`;

						return result;
					}
				} else {
					return ``;
				}
			},
		},

		/**
		 * Run whenever an entity that required this dependency is destroyed.
		 * @method onDie
		 * @param {object} context - The instance that is being destroyed
		 */
		onDie: {
			public: true,
			value(context) {
				const { instances } = this;
				const { subscribers } = instances;

				// instance that has requested this dependency was destroyed
				this.extract(subscribers, ({ instance }) => instance === context);
				this.checkSessionUsage();
			},
		},
	},
});

//# sourceMappingURL=ReservedSeating.js.map